import React, { Dispatch, SetStateAction, useContext, useEffect, useMemo, useRef, useState } from 'react';
import classes from './PostEditorModal.module.scss';
import scssVars from '../../../assets/scss/exportedVars.module.scss';
import modalClasses from '../../../components/containers/Modal/Modal.module.scss';
import { CSSTransition } from 'react-transition-group';
import { MandatoryChildren } from '../../../models/children-props';
import classNames from 'classnames';
import { Loader } from '../../../components/containers/Loader';
import { SquareButton } from '../../../components/actions/SquareButton';
import { SquareButtonIconsEnum } from '../../../constants/square-button-icons';
import { useDimensions } from '../../../hooks/useDimensions';
import { PostEditorModalFooter } from './PostEditorModalFooter';
import { FieldValues, SubmitErrorHandler, UseFormReturn } from 'react-hook-form';
import { Portal } from '../../../components/containers/Portal';
import { SubmitHandler } from 'react-hook-form/dist/types/form';
import { useTimeout } from 'primereact/hooks';
import { PostEditorContextEnum, PostEditorSubmitData } from '../../../models/post-editor';
import { AppContext, isFromChromeExtension } from 'providers/AppProvider';
import { useBreakpoints } from '../../../hooks/useBreakpoints';

export type PostEditorModalViewProps = MandatoryChildren<{
  //
  animate: boolean;
  loading: boolean;
  onClose: VoidFunction;
  setExit: Dispatch<SetStateAction<boolean>>;
  title: string | JSX.Element;
  formInstance: UseFormReturn;
  onSubmit: SubmitHandler<PostEditorSubmitData>;
  onError: SubmitErrorHandler<PostEditorSubmitData>;
  onDeletePostGroup?: (payload: number) => void;
  setInitialised?: Dispatch<SetStateAction<boolean>>;
  onboardingWidgetHeight: number;
}>;

export const PostEditorModalView = (props: PostEditorModalViewProps): JSX.Element => {
  const [mount, setMount] = useState<boolean>(false);
  const cssRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { hasScrollbar } = useDimensions(contentRef);
  const [isScrolled, setIsScrolled] = useState(false);
  const { postEditorContext } = useContext(AppContext);
  const breakpoint = useBreakpoints();

  const postEditorHeightCalculations = useMemo(() => {
    const inside = new RegExp(/calc\(\s*([^)]+?)\s*\)/);
    let container: string;
    let content: string;
    // container
    if (isFromChromeExtension) {
      container = props.onboardingWidgetHeight
        ? `calc(${scssVars['post-editor-container-chrome-extension-height']} - ${props.onboardingWidgetHeight}px)`
        : scssVars['post-editor-container-chrome-extension-height'];
    } else if (breakpoint === 'xxlarge') {
      container = props.onboardingWidgetHeight
        ? `calc(${scssVars['post-editor-container-xxlarge-height']} - ${props.onboardingWidgetHeight}px)`
        : scssVars['post-editor-container-xxlarge-height'];
    } else {
      container = props.onboardingWidgetHeight
        ? `calc(${scssVars['post-editor-container-mobile-height']} - ${props.onboardingWidgetHeight}px)`
        : scssVars['post-editor-container-mobile-height'];
    }
    // content
    if (isFromChromeExtension) {
      if (breakpoint === 'small') {
        const strippedValues = scssVars['post-editor-content-regular-mobile-height'].match(inside);
        content =
          props.onboardingWidgetHeight && strippedValues
            ? `calc(${strippedValues[1]} - ${props.onboardingWidgetHeight}px)`
            : scssVars['post-editor-content-regular-mobile-height'];
      } else {
        const strippedValues = scssVars['post-editor-content-chromeextension-tablet-portrait-height'].match(inside);
        content =
          props.onboardingWidgetHeight && strippedValues
            ? `calc(${strippedValues[1]} - ${props.onboardingWidgetHeight}px)`
            : scssVars['post-editor-content-chromeextension-tablet-portrait-height'];
      }
    } else if (postEditorContext.type === PostEditorContextEnum.import) {
      if (breakpoint === 'xxlarge') {
        const strippedValues = scssVars['post-editor-content-bulk-importer-xxlarge-height'].match(inside);
        content =
          props.onboardingWidgetHeight && strippedValues
            ? `calc(${strippedValues[1]} - ${props.onboardingWidgetHeight}px)`
            : scssVars['post-editor-content-bulk-importer-xxlarge-height'];
      } else {
        const strippedValues = scssVars['post-editor-content-bulk-importer-height'].match(inside);
        content =
          props.onboardingWidgetHeight && strippedValues
            ? `calc(${strippedValues[1]} - ${props.onboardingWidgetHeight}px)`
            : scssVars['post-editor-content-bulk-importer-height'];
      }
    } else {
      if (breakpoint === 'small') {
        const strippedValues = scssVars['post-editor-content-regular-mobile-height'].match(inside);
        content =
          props.onboardingWidgetHeight && strippedValues
            ? `calc(${strippedValues[1]} - ${props.onboardingWidgetHeight}px)`
            : scssVars['post-editor-content-regular-mobile-height'];
      } else if (breakpoint === 'xxlarge') {
        const strippedValues = scssVars['post-editor-content-regular-xxlarge-height'].match(inside);
        content =
          props.onboardingWidgetHeight && strippedValues
            ? `calc(${strippedValues[1]} - ${props.onboardingWidgetHeight}px)`
            : scssVars['post-editor-content-regular-xxlarge-height'];
      } else {
        const strippedValues = scssVars['post-editor-content-regular-tablet-portrait-height'].match(inside);
        content =
          props.onboardingWidgetHeight && strippedValues
            ? `calc(${strippedValues[1]} - ${props.onboardingWidgetHeight}px)`
            : scssVars['post-editor-content-regular-tablet-portrait-height'];
      }
    }
    return { container, content };
  }, [breakpoint, props.onboardingWidgetHeight, postEditorContext.type]);

  useEffect(() => {
    setMount(props.animate);
    return () => setMount(false);
  }, [props.animate]);

  const refContent = contentRef.current;

  useEffect(() => {
    if (refContent) {
      const handleScroll = () => {
        const hasScrolled = refContent.scrollTop > 0;
        setIsScrolled(hasScrolled);
      };
      refContent.addEventListener('scroll', handleScroll);
      handleScroll();
      return () => refContent.removeEventListener('scroll', handleScroll);
    }
  }, [hasScrollbar, refContent]);

  // This timeout is used since we have a fadeIN effect with transition before the component is properly mounted.
  // That's why we wait before we say hay, it has been initialized.
  useTimeout(
    () => {
      props.setInitialised && props.setInitialised(true);
    },
    500,
    props.setInitialised && mount
  );
  const isBulkImport = postEditorContext.type === PostEditorContextEnum.import;
  const modalWrapperClasses = classNames(classes.PostEditorModal, 'grid-container', 'full', {
    [classes.PostEditorModalChromeExtension]: isFromChromeExtension
  });
  const modalHeaderClasses = classNames(modalClasses.Header, classes.Header, 'grid-x', 'align-justify', {
    [classes.HeaderIsScrolled]: hasScrollbar && isScrolled,
    [classes.HeaderBulkImporter]: isBulkImport
  });
  const footerContainerClasses = classNames(classes.ButtonContainer, 'grid-x', 'align-justify', {
    [classes.ButtonContainerBulkImporter]: isBulkImport
  });
  const contentClasses = classNames(classes.Content, 'editor-grid', {
    [classes.ContentBulkImporter]: isBulkImport,
    [classes.ContentChromeExtension]: isFromChromeExtension
  });

  const isSubmitHandlerGuard = (value: SubmitHandler<PostEditorSubmitData>): value is SubmitHandler<FieldValues> => {
    // this is a fake guard since value.args is always of type FieldValues, since FieldValues is a simple Record
    return true;
  };

  return (
    <Portal>
      <CSSTransition
        in={mount}
        timeout={300}
        classNames={'fade'}
        unmountOnExit
        onExited={() => props.setExit(true)}
        nodeRef={cssRef}>
        {isSubmitHandlerGuard(props.onSubmit) ? (
          <form onSubmit={props.formInstance.handleSubmit(props.onSubmit, props.onError)}>
            <div
              className={modalWrapperClasses}
              ref={cssRef}
              style={{
                height: props.onboardingWidgetHeight ? `calc(100vh - ${props.onboardingWidgetHeight}px)` : '100vh'
              }}>
              <div className={`grid-y align-center full-height`}>
                <div className={`grid-x align-center`}>
                  <div
                    style={{
                      height: postEditorHeightCalculations.container
                    }}
                    className={classNames(classes.ModalContainer, {
                      [classes.ModalContainerChromeExtension]: isFromChromeExtension
                    })}>
                    <div className={modalHeaderClasses}>
                      {typeof props.title === 'string' ? (
                        <h3
                          className={classNames(classes.PostEditorTitle, {
                            [classes.PostEditorTitleHasScroll]: hasScrollbar
                          })}>
                          {props.title}
                        </h3>
                      ) : (
                        props.title
                      )}
                      <div className={classNames(classes.CloseButton)}>
                        <SquareButton type={'transparent'} icon={SquareButtonIconsEnum.close} action={props.onClose} />
                      </div>
                    </div>
                    {/* post-editor-scrollable-content id is used in SlateEditor.view, since we could not pass ref. NOTE: don't change it */}
                    <div
                      className={contentClasses}
                      ref={contentRef}
                      style={{
                        height: postEditorHeightCalculations.content
                      }}
                      id={'post-editor-scrollable-content'}>
                      {props.children}
                    </div>
                    {props.loading && (
                      <Loader variation={'dark'} size={'large'} type={'overlay'} containedOverlay roundedBg />
                    )}
                    <div className={footerContainerClasses}>
                      <PostEditorModalFooter formInstance={props.formInstance} loading={props.loading} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        ) : null}
      </CSSTransition>
    </Portal>
  );
};
